import React from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDateShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, CalendarBlank, MapPin, Truck, Trash, ChatCircleDots, Pencil, X } from "@phosphor-icons/react";
import { buildWhatsAppUrl, paymentReminderMessage } from "@/lib/whatsapp";

/**
 * Compact quick-view modal for an order. Opens from Orders card click.
 * Shows: header (order no + name + PENDING pill), mobile/order-date/address/dispatch,
 * items table with HSN column, totals grid, action row (Delete/WhatsApp/Edit/Dispatch).
 */
export default function OrderQuickView({ order, open, onOpenChange, onChanged }) {
    const navigate = useNavigate();
    if (!order) return null;

    const c = order.customer_snapshot || {};
    const paid = Number(order.paid_amount || 0);
    const balance = Number(order.balance_amount ?? (Number(order.total || 0) - paid));
    const disp = order.dispatch || {};
    const dispatchDate = disp.expected_delivery || disp.dispatched_at;

    const statusMap = {
        draft:     { bg: "#F3F4F6", fg: "#525252", label: "Draft" },
        pending:   { bg: "#FEF3C7", fg: "#B45309", label: "Pending" },
        completed: { bg: "#DCFCE7", fg: "#15803D", label: "Completed" },
        cancelled: { bg: "#FEE2E2", fg: "#DC2626", label: "Cancelled" },
    };
    const isDispatched = order.fulfillment_stage === "dispatched" || order.fulfillment_stage === "delivered";
    const pill = isDispatched
        ? { bg: "#DBEAFE", fg: "#1D4ED8", label: order.fulfillment_stage === "delivered" ? "Delivered" : "Dispatched" }
        : (statusMap[order.status] || statusMap.pending);

    const remove = async () => {
        if (!window.confirm(`Cancel order ${order.order_no}? Ye action reverse ho sakta hai but stock ledger update hoga.`)) return;
        try {
            await api.patch(`/sales/${order.id}/status`, { status: "cancelled" });
            toast.success("Order cancelled");
            onOpenChange(false);
            onChanged && onChanged();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const dispatchNow = () => {
        onOpenChange(false);
        navigate(`/orders/${order.id}`);
    };

    const waUrl = (() => {
        const msg = (balance > 0.01)
            ? paymentReminderMessage({ customerName: c.name || order.customer_name, pendingAmount: balance, invoiceNo: order.order_no })
            : `Namaste ${c.name || order.customer_name},\nAapke order ${order.order_no} ki update:\nTotal: ${formatCurrency(order.total)}\nPaid: ${formatCurrency(paid)}\n— BS Fiber Product`;
        return buildWhatsAppUrl(c.whatsapp || c.mobile, msg);
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="order-quickview">
                {/* Header */}
                <div className="p-5 pb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{order.order_no}</div>
                        <div className="text-2xl font-extrabold font-display tracking-tight mt-0.5 truncate">{c.name || order.customer_name}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap shrink-0"
                          style={{ background: pill.bg, color: pill.fg }}>{pill.label}</span>
                </div>

                {/* Meta */}
                <div className="px-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <MetaLine Icon={Phone} label="MOBILE" value={c.mobile || "—"} mono />
                    <MetaLine Icon={CalendarBlank} label="ORDER DATE" value={formatDateShort(order.created_at)} />
                    <MetaLine Icon={MapPin} label="ADDRESS" value={c.address || order.delivery_address || "—"} multiline />
                    <MetaLine Icon={Truck} label="PLANNED DISPATCH" value={dispatchDate ? formatDateShort(dispatchDate) : "—"} />
                </div>

                {/* Items table */}
                <div className="mx-5 mt-4 rounded-md border border-neutral-200 overflow-hidden">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left" style={{ background: "#F5F5F5" }}>
                                <th className="py-2 px-3 font-bold uppercase tracking-wider text-neutral-500">Product</th>
                                <th className="py-2 px-3 font-bold uppercase tracking-wider text-neutral-500">HSN</th>
                                <th className="py-2 px-3 text-right font-bold uppercase tracking-wider text-neutral-500">Qty</th>
                                <th className="py-2 px-3 text-right font-bold uppercase tracking-wider text-neutral-500">Rate</th>
                                <th className="py-2 px-3 text-right font-bold uppercase tracking-wider text-neutral-500">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {order.items.map((it, i) => (
                                <tr key={i} className="border-t border-neutral-100">
                                    <td className="py-2 px-3">
                                        <div className="font-semibold">{it.product_name}</div>
                                        <div className="text-[10px] font-mono text-neutral-500">{it.sku}</div>
                                    </td>
                                    <td className="py-2 px-3 font-mono">{it.hsn_code || "—"}</td>
                                    <td className="py-2 px-3 text-right tabular">{it.quantity} pcs</td>
                                    <td className="py-2 px-3 text-right tabular">{formatCurrency(it.unit_price)}</td>
                                    <td className="py-2 px-3 text-right tabular font-bold">{formatCurrency(it.line_total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div className="mx-5 mt-4 grid grid-cols-3 gap-3">
                    <div className="border border-neutral-200 rounded-md p-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Total</div>
                        <div className="text-lg font-extrabold font-display tabular mt-0.5">{formatCurrency(order.total)}</div>
                    </div>
                    <div className="rounded-md p-3" style={{ background: "#F5F5F5" }}>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Advance</div>
                        <div className="text-lg font-extrabold font-display tabular mt-0.5" style={{ color: "#15803D" }}>{formatCurrency(paid)}</div>
                    </div>
                    <div className="rounded-md p-3" style={{ background: "#FEF2F2", borderColor: "#FECACA" }}>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Baaki</div>
                        <div className="text-lg font-extrabold font-display tabular mt-0.5" style={{ color: balance > 0 ? "#DC2626" : "#15803D" }}>{formatCurrency(balance)}</div>
                    </div>
                </div>

                {/* Actions */}
                <div className="p-5 pt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <button onClick={remove} className="h-11 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center gap-2 text-sm font-semibold" data-testid="qv-delete">
                        <Trash size={16} /> Delete
                    </button>
                    <a href={waUrl} target="_blank" rel="noreferrer" className="h-11 rounded-lg border-2 flex items-center justify-center gap-2 text-sm font-semibold" style={{ borderColor: "#15803D", color: "#15803D" }} data-testid="qv-whatsapp">
                        <ChatCircleDots size={16} weight="duotone" /> WhatsApp
                    </a>
                    <button onClick={() => { onOpenChange(false); navigate(`/orders/${order.id}`); }} className="h-11 rounded-lg border border-neutral-300 hover:bg-neutral-50 flex items-center justify-center gap-2 text-sm font-semibold text-neutral-800" data-testid="qv-edit">
                        <Pencil size={16} /> Edit
                    </button>
                    <button onClick={dispatchNow} className="h-11 rounded-lg text-white flex items-center justify-center gap-2 text-sm font-bold" style={{ backgroundColor: "#15803D" }} data-testid="qv-dispatch">
                        <Truck size={16} /> Dispatch Karein
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function MetaLine({ Icon, label, value, mono, multiline }) {
    return (
        <div className="flex items-start gap-2">
            <Icon size={16} weight="duotone" className="text-neutral-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
                <div className={"text-sm font-semibold text-neutral-900 " + (mono ? "font-mono " : "") + (multiline ? "break-words" : "truncate")}>{value}</div>
            </div>
        </div>
    );
}
